/**
 * THE DRIVE HUD (B8) — design-spec §E.6, §A.2 (centred in the safe area,
 * bottom 112, h 56, max 560), §F drive.*.
 *
 *  caption    "NOW ON" over the road in Newsreader 22/360, from drive:progress.
 *             Cross-fade out 294 leave / in 420 settle, minimum hold 2500 ms,
 *             stretches driven in under 2500 ms are skipped (captions.ts).
 *  countdown  "{km} km · {min} min to go" (km 1 dp, minutes rounded up), ≤ 10 Hz,
 *             written to the DOM directly.
 *  end        "End the drive" and Esc call onStop.
 *  arrive     "Arrived at {name}", held 2400 ms (the card waits for it).
 *  stop       drive:end "interrupted" → the HUD leaves (294) and a toast says
 *             "The drive stopped where you took over." with Resume (6 s).
 *
 * Reduced motion has no ride (the card's turn list replaces it), so this HUD
 * only appears in phase "drive".
 */
import "./card.css";
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import { displayTravel, type NameSpan } from "@/contracts/data";
import type { DriveHudProps } from "@/contracts/ui";
import { Button, Meta, cx, useReducedMotion } from "@/design";
import { on } from "@/store/bus";
import { selectPlace } from "@/store/selectors";
import { getApp, useApp } from "@/store/store";
import { START, feedCaption, initialCaption, tickCaption, type CaptionState } from "./captions";
import { LEAVE_MS } from "./cardState";
import { getDriveStarter, setDriveHold } from "./driveState";
import { arrivalMeta, driveLeft, mergeSpans } from "./format";
import { spanTravelMs, tdriveMs } from "./timing";
import { ToastHost, hideToast, showToast } from "./Toast";

export interface DriveHudOwnProps extends DriveHudProps {
  /** "Resume" on the stop toast. Default: start the drive the way the card does (its onDrive). */
  onResume?: () => void;
}

/** §E.6: "Arrived at {name}" is held this long. */
export const ARRIVE_HOLD_MS = 2400;
/** §E.6: the stop toast shows for 6 s. */
export const STOP_TOAST_MS = 6000;
/** §E.6: the countdown updates at ≤ 10 Hz. */
const COUNT_EVERY_MS = 100;

type Cap = { key: number; kind: "start" | "road" | "arrived"; text: string; meta: string };
type Hud = { id: string; key: number; leaving: boolean };

/** The merged span with this name that contains s (metres), for its travel time. */
function spanAt(spans: NameSpan[], name: string, s: number): NameSpan | null {
  let best: NameSpan | null = null;
  for (const sp of spans) {
    if (sp.name !== name) continue;
    if (s >= sp.fromM - 30 && s <= sp.toM + 30) return sp;
    if (!best || Math.abs(sp.fromM - s) < Math.abs(best.fromM - s)) best = sp;
  }
  return best;
}

export function DriveHud({ onStop, onResume }: DriveHudOwnProps) {
  const phase = useApp((s) => s.phase);
  const selectedId = useApp((s) => s.selectedId);
  const layout = useApp((s) => s.layout);
  const kiosk = useApp((s) => s.kiosk) || layout === "kiosk";
  const iLeft = useApp((s) => s.insets.left);
  const iRight = useApp((s) => s.insets.right);
  const iBottom = useApp((s) => s.insets.bottom);
  const reduced = useReducedMotion();
  const keys = useRef(0);

  const [hud, setHud] = useState<Hud | null>(null);
  const [arrived, setArrived] = useState<string | null>(null); // place id held after arrival
  const [cap, setCap] = useState<Cap | null>(null);
  const [leftOn, setLeftOn] = useState(true);
  const leftRef = useRef<HTMLSpanElement>(null);
  const hudRef = useRef<HTMLDivElement>(null);
  const capState = useRef<CaptionState | null>(null);

  const driving = phase === "drive" && !!selectedId;
  const wanted = driving || arrived != null;

  /* ── mount / leave ── */
  useLayoutEffect(() => {
    const id = arrived ?? selectedId;
    if (wanted && id) {
      setHud((h) => (h && h.id === id && !h.leaving ? h : { id, key: ++keys.current, leaving: false }));
    } else {
      setHud((h) => (h && !h.leaving ? { ...h, leaving: true } : h));
    }
  }, [wanted, arrived, selectedId]);
  const hudKey = hud?.key;
  const hudLeaving = hud?.leaving;
  useEffect(() => {
    if (!hudLeaving || hudKey == null) return undefined;
    const t = setTimeout(() => setHud((h) => (h && h.key === hudKey && h.leaving ? null : h)), reduced ? 160 : LEAVE_MS);
    return () => clearTimeout(t);
  }, [hudLeaving, hudKey, reduced]);

  /* ── a fresh drive: the start line, the full countdown ── */
  const hudId = hud && !hud.leaving ? hud.id : null;
  useLayoutEffect(() => {
    if (!hudId || arrived) return undefined;
    const now = performance.now();
    capState.current = initialCaption(now);
    const p = selectPlace(getApp(), hudId);
    setCap({ key: ++keys.current, kind: "start", text: "From Ládera City", meta: p ? p.name : "" });
    setLeftOn(true);
    const t = p ? displayTravel(p) : null;
    if (leftRef.current && t) leftRef.current.textContent = driveLeft(t.km, t.minutes);
    hideToast("drive");
    // Keyboard users land on "End the drive" when the card they drove from goes. After the first frame of
    // the drive (card exit, chrome dim) has rendered: focus() needs layout, and forcing it in the task
    // that starts the drive would lay the whole page out twice (QA L2-06).
    let post: ReturnType<typeof setTimeout> | undefined;
    const raf = requestAnimationFrame(() => {
      post = setTimeout(() => {
        const a = document.activeElement;
        if (!a || a === document.body || a.closest("#ld-card") || a.closest("[inert]"))
          hudRef.current?.querySelector<HTMLButtonElement>(".ld-loc-card-hud__end")?.focus({ preventScroll: true });
      }, 0);
    });
    return () => {
      cancelAnimationFrame(raf);
      if (post) clearTimeout(post);
    };
    // arrived is read, not a trigger: this runs for each new drive only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hudId]);

  /* ── progress → captions + countdown ── */
  useEffect(() => {
    if (!hudId) return undefined;
    const st = getApp();
    const place = selectPlace(st, hudId);
    const travel = place ? displayTravel(place) : null;
    const route = st.routes?.routes.find((r) => r.id === place?.route?.routeId) ?? null;
    const spans = route ? mergeSpans(route.nameSpans) : [];
    const L = route?.lengthM ?? 0;
    const T = tdriveMs(L / 1000);
    let lastWrite = 0;
    let arrivedOnce = false;

    const arrive = () => {
      if (arrivedOnce || !place) return;
      arrivedOnce = true;
      setCap({ key: ++keys.current, kind: "arrived", text: `Arrived at ${place.name}`, meta: arrivalMeta(place) ?? "" });
      setLeftOn(false);
      setArrived(place.id);
    };

    const offProgress = on("drive:progress", (e) => {
      if (e.placeId !== hudId) return;
      const now = performance.now();
      if (e.p >= 1) return arrive();
      // countdown (≤ 10 Hz); from the card's own numbers so they always agree
      if (now - lastWrite >= COUNT_EVERY_MS && leftRef.current) {
        lastWrite = now;
        leftRef.current.textContent = travel ? driveLeft(travel.km * (1 - e.p), travel.minutes * (1 - e.p)) : driveLeft(e.kmLeft, e.minutesLeft);
      }
      // captions
      let cs = capState.current ?? initialCaption(now);
      const before = cs.shown;
      cs = tickCaption(cs, now);
      let spanMs: number | undefined;
      if (e.caption && L > 0) {
        const sp = spanAt(spans, e.caption, e.p * L);
        if (sp) spanMs = spanTravelMs(sp.fromM, sp.toM, L, T);
      }
      cs = feedCaption(cs, e.caption, now, spanMs);
      capState.current = cs;
      if (cs.shown !== before && cs.shown !== START) setCap({ key: ++keys.current, kind: "road", text: cs.shown, meta: "Now on" });
    });
    const offEnd = on("drive:end", (e) => {
      if (e.placeId !== hudId) return;
      if (e.reason === "done") return arrive();
      if (e.reason === "interrupted") {
        const resume = onResume ?? getDriveStarter();
        showToast({
          id: "drive",
          text: "The drive stopped where you took over.",
          icon: "car",
          action: resume ? { label: "Resume", onClick: resume } : undefined,
          ms: STOP_TOAST_MS,
        });
      }
      setArrived(null);
      setHud((h) => (h && !h.leaving ? { ...h, leaving: true } : h));
    });
    return () => {
      offProgress();
      offEnd();
    };
  }, [hudId, onResume]);

  /* ── the arrival hold: the card waits, then the HUD goes ── */
  useEffect(() => {
    if (!arrived) return undefined;
    setDriveHold(true);
    const t = setTimeout(() => {
      setDriveHold(false);
      setArrived(null);
    }, ARRIVE_HOLD_MS);
    return () => {
      clearTimeout(t);
      setDriveHold(false);
    };
  }, [arrived]);

  /* ── Esc ends the drive (§H.1: the drive is above the card) ── */
  useEffect(() => {
    if (!driving) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      e.preventDefault();
      onStop();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [driving, onStop]);

  /* ── the outgoing caption cross-fades out (294 leave) ── */
  const [outCap, setOutCap] = useState<Cap | null>(null);
  const prevCap = useRef<Cap | null>(null);
  useLayoutEffect(() => {
    const prev = prevCap.current;
    prevCap.current = cap;
    if (prev && cap && prev.key !== cap.key) {
      setOutCap(prev);
      const t = setTimeout(() => setOutCap((o) => (o && o.key === prev.key ? null : o)), LEAVE_MS);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [cap]);

  // mobile: 12 above the sheet, whatever its snap (insets.bottom = sheet + 16)
  const wrapStyle: CSSProperties =
    layout === "mobile"
      ? { bottom: iBottom > 0 ? iBottom - 4 : undefined }
      : { left: iLeft > 0 ? Math.max(12, iLeft - 32) : undefined, right: iRight > 0 ? Math.max(12, iRight - 32) : undefined };
  const wrapCls = cx("ld-loc-card-hudwrap", layout === "mobile" && "ld-loc-card-hudwrap--mobile", kiosk && "ld-loc-card-hudwrap--kiosk");

  return (
    <>
      {hud && (
        <div className={wrapCls} style={wrapStyle}>
          <div
            key={hud.key}
            ref={hudRef}
            role="group"
            aria-label="The drive"
            className={cx("ld-loc-card-hud ld-glass", hud.leaving ? "is-leaving" : "ld-in")}
          >
            <div className="ld-loc-card-hud__cap" aria-live="off">
              {outCap && <Caption key={outCap.key} cap={outCap} out />}
              {cap && <Caption key={cap.key} cap={cap} />}
            </div>
            <span ref={leftRef} className={cx("ld-loc-card-hud__left", !leftOn && "is-off")} aria-hidden={!leftOn || undefined} />
            <Button variant="glass" size="sm" onClick={onStop} className="ld-loc-card-hud__end" data-ld-drive-focus="">
              End the drive
            </Button>
          </div>
        </div>
      )}
      <ToastHost />
    </>
  );
}

function Caption({ cap, out }: { cap: Cap; out?: boolean }) {
  return (
    <div className={cx("ld-loc-card-hud__capitem", out ? "ld-loc-card-hud__capitem--out" : "ld-loc-card-hud__capitem--in")} aria-hidden={out || undefined}>
      {cap.meta && <Meta className="ld-loc-card-hud__meta">{cap.meta}</Meta>}
      <span className="ld-loc-card-hud__road">{cap.text}</span>
    </div>
  );
}
